<template>
  <div class="mx-3">
    <v-row>
      <v-col
        cols="12"
        lg="3"          
        md="12"
        sm="12"
        xl="2"
      >
        <setup-leaves-leave-entitlement-dialog-create
          v-if="$hasPermissionTo(['create leave entitlements'])"
          @created="loadData"
        />
      </v-col>

      <v-col
        cols="12"
        lg="3"          
        md="12"
        sm="12"
        xl="2"
      >
        <setup-leaves-leave-entitlement-dialog-copy
          v-if="$hasPermissionTo(['create leave entitlements'])"
          @copied="loadData"
        />
      </v-col>
      <v-spacer />

      <v-col
        class="py-0"
        cols="12"
        lg="3"          
        md="12"
        sm="12"
        xl="2"
      >
        <v-autocomplete
          v-model="leaveType"
          :items="leaveTypes"
          multiple
          item-text="name"
          item-value="id"
          hide-no-data
          chips
          small-chips
          deletable-chips
          hide-selected
          label="Leave Type Filter"
          placeholder="Choose leave type"
          @change="loadData"
        />
      </v-col>

      <v-col
        class="py-0"
        cols="12"
        lg="3"          
        md="12"
        sm="12"
        xl="2"
      >
        <v-autocomplete
          v-model="selectedYear"
          :items="leaveYears"
          item-text="name"
          item-value="id"
          hide-no-data
          chips
          small-chips
          deletable-chips
          hide-selected
          label="Year Filter"
          placeholder="Choose year"
          @change="loadData"
        />
      </v-col>

      <v-col
        class="py-0"
        cols="12"
        lg="3"          
        md="12"
        sm="12"
        xl="2"
      >
        <v-text-field
          v-model="search"
          placeholder="Key in employee name here"
          append-icon="search"
          label="Search Employee"
          hide-no-data
          hide-selected
          clearable 
        />
      </v-col>
    </v-row>

    <v-data-table
      :search="search"
      single-expand
      :headers="headers"
      :items="entitlements"
      :loading="loading"
    >
      <template v-slot:item.employee.person.name="{ item }">
        <span :class="item.employee.end_date && ($moment(item.employee.end_date) <= $moment()) ? 'red--text' : ''">
          {{ item.employee.person.name }}
        </span>
        <setup-leaves-leave-entitlement-dialog-list-all
          v-if="item.leave_entitlement_adjustments.length > 0 && $hasPermissionTo(['view leave entitlements adjustments'])"
          :entitlement="item"
          @updated="loadData"
        />
      </template>

      <template v-slot:item.leave_type.name="{ item }">
        {{ item.leave_type.name }}
        <!-- ONLY FOR PRORATED LEAVES -->
        <setup-leaves-leave-entitlement-dialog-forecast-prorated-days
          v-if="item.leave_type.is_prorated == 1 && $hasPermissionTo(['calculate forecast leave entitlement prorated days'])"
          :entitlement="item"
        />
        <!-- ONLY FOR PRORATED LEAVES -->
      </template>

      <template v-slot:item.entitled_days="{ item }">
        <span style="display:inline-block;width:40px">
          {{ item.leave_type.name === "COVID - PAID TIME OFF" ? '5.00' : item.entitled_days }}
        </span>
      </template>

      <template v-slot:item.prorated_days="{ item }">
        <span style="display:inline-block;width:40px">
          {{ item.prorated_days }}
        </span>
      </template>

      <template v-slot:item.entitled_days_taken="{ item }">
        <div>
          <span v-if="item.entitled_days_taken">
            {{ (Number(item.entitled_days_taken) + Number(item.carried_forward_days_taken)).toFixed(2) || '0.00' }}
            <setup-leaves-leave-entitlement-dialog-view-leave-applications
              v-if="$hasPermissionTo(['view leave applications'])"
              :entitlement="item"
              :selected-year="selectedYear"
              @cancel="loadData"
            />
          </span>
          <span v-else>
            N/A
          </span>
        </div>
      </template>

      <template v-slot:item.carried_forward_days="{ item }">
        <div>
          <span v-if="(item.carried_forward_days && item.leave_type.name !== 'COVID - PAID TIME OFF')">
            {{ item.carried_forward_days }}
          </span>
          <span v-else>
            N/A
          </span>
        </div>
      </template>

      <template v-slot:item.total_adjusted_days="{ item }">
        <span style="display:inline-block;width:40px">
          {{ item.total_adjusted_days.toFixed(2) || '0.00' }}
        </span>
        <setup-leaves-leave-entitlement-dialog-adjustment
          v-if="$hasPermissionTo(['adjust leave entitlements'])"
          :entitlement="item"
          @updated="loadData"
        />
      </template>

      <template v-slot:item.maximum_carry_forward_amount="{ item }">
        <div>
          <span v-if="item.maximum_carry_forward_amount">
            {{ item.maximum_carry_forward_amount }}
          </span>
          <span v-else>
            N/A
          </span>
        </div>
      </template>

      <template v-slot:item.available_days="{ item }">
        <div>
          <span v-if="item.available_days">
            {{ item.leave_type.name === "COVID - PAID TIME OFF" ? null : (item.available_days || '0.00') }}
          </span>
          <span v-else>
            N/A
          </span>
        </div>
      </template>

      <template v-slot:item.available_carry_forward="{ item }">
        <div>
          <span
            v-if="item.available_carry_forward"
            class="font-weight-bold"
          >
            {{ item.available_carry_forward || '0.00' }}
          </span>
          <span v-else>
            N/A
          </span>
        </div>
      </template>

      <template v-slot:item.action="{ item }"> 
        <!-- <setup-leaves-leave-entitlement-dialog-edit
          :entitlement="item"
          @updated="loadData"
        />     -->
        <setup-leaves-leave-entitlement-dialog-delete
          v-if="$hasPermissionTo(['delete leave entitlements'])"
          :entitlement="item"
          @deleted="loadData"
        />
      </template>
    </v-data-table>

    <span class="subtitle-2 font-weight-medium font-italic">
      <div>
        <span class="font-weight-bold">Legend: </span>
        <span class="font-weight-bold">ED</span> = Entitled Days, 
        <span class="font-weight-bold">PD</span> = Prorated Days, 
        <span class="font-weight-bold">AD</span> = Adjusted Days, 
        <span class="font-weight-bold">CFD</span> = Carried Forward Days, 
        <span class="font-weight-bold">CF</span> = Carried Forward, 
        <span class="font-weight-bold">MCFD</span> = Maximum Carry Forward Days,
        <span class="font-weight-bold red--text">Name in Red</span> = Resigned Employee
      </div>
    </span>
  </div>
</template>

<script>
  export default {
    components: {
      SetupLeavesLeaveEntitlementDialogCreate: () => import('@/components/hris/setup/leaves/leave-entitlement/DialogCreate'),
      SetupLeavesLeaveEntitlementDialogCopy: () => import('@/components/hris/setup/leaves/leave-entitlement/DialogCopy'),
      // SetupLeavesLeaveEntitlementDialogEdit: () => import('@/components/hris/setup/leaves/leave-entitlement/DialogEdit'),
      SetupLeavesLeaveEntitlementDialogDelete: () => import('@/components/hris/setup/leaves/leave-entitlement/DialogDelete'),
      SetupLeavesLeaveEntitlementDialogAdjustment: () => import('@/components/hris/setup/leaves/leave-entitlement/DialogAdjustment'),
      SetupLeavesLeaveEntitlementDialogViewLeaveApplications: () => import('@/components/hris/setup/leaves/leave-entitlement/DialogViewLeaveApplications'),
      SetupLeavesLeaveEntitlementDialogForecastProratedDays: () => import('@/components/hris/setup/leaves/leave-entitlement/DialogForecastProratedDays'),
      SetupLeavesLeaveEntitlementDialogListAll: () => import('@/components/hris/setup/leaves/leave-entitlement/DialogListAll'),
    },
    data () {
      return {
        leaveType: [1],
        selectedYear: this.$moment().format('YYYY'),
        headers: [
          {
            text: 'Name',
            align: 'left',
            value: 'employee.person.name',
          },
          {
            text: 'Leave Type',
            align: 'left',
            value: 'leave_type.name',
          },
          {
            text: 'Start Date',
            align: 'left',
            sortable: false,
            value: 'start_date',
          },
          {
            text: 'ED',
            align: 'left',
            sortable: false,
            value: 'entitled_days',
          },
          {
            text: 'PD',
            align: 'center',
            sortable: false,
            value: 'prorated_days',
          },
          {
            text: 'CFD',
            align: 'left',
            sortable: false,
            value: 'carried_forward_days',
          },
          {
            text: 'AD',
            align: 'center',
            sortable: false,
            value: 'total_adjusted_days',
          },
          {
            text: 'ED Taken',
            align: 'left',
            sortable: false,
            value: 'entitled_days_taken',
          },
          {
            text: 'Balance',
            align: 'left',
            sortable: false,
            value: 'available_days',
          },
          {
            text: 'MCFD',
            align: 'left',
            sortable: false,
            value: 'maximum_carry_forward_amount',
          },
          {
            text: '',
            sortable: false,
            value: 'action',
          },
        ],  
        search: '',
        loading: false,
        entitlements: [],
        leaveTypes: [],
        leaveYears: [],
      }
    },
    created () {
      this.loadData()
    },
    methods: {
      loadData () {
        this.loading = true
        Promise.all([
          this.$api.hrisLeaveTypes().list(),
          this.$api.leaveEntitlements().list(this.prepareFormData(this.leaveType)),
        ]).then(([leaveTypes, entitlements]) => {
          this.leaveTypes = _.orderBy(leaveTypes.data, ['name'], ['asc'])   
          this.entitlements = _.orderBy(entitlements.data, ['employee.person.name', 'leave_type.name'], ['asc', 'asc'])

          this.leaveYears = [
            this.$moment().add(-1, 'years').format('YYYY'),
            this.$moment().format('YYYY'),
            this.$moment().add(1, 'years').format('YYYY'),
          ]

          this.loading = false
        })
      },
      prepareFormData (leaveType) {
        return {
          selectedLeaveTypes: leaveType,
          selectedYear: this.selectedYear,
        }
      },
    },
  }
</script>

<style scoped>
  ::v-deep .v-toolbar__content{
    margin-left:0px !important;
  }

  ::v-deep .v-table__overflow{
    overflow-x: hidden;
  }
</style>
